import { useEffect, useState } from 'react';
import type { InclusionStatus } from '../types';

interface Props {
  kind: 'tax' | 'service';
  defaults?: { included?: InclusionStatus | undefined; rate?: number | undefined };
  onBack: () => void;
  onNext: (values: { included: InclusionStatus; rate?: number }) => void;
}

const LABELS = {
  tax: {
    title: 'Tax settings',
    includePrompt: 'Do your prices already include tax, or should we add it on top?',
    rateHelp: 'The tax rate in your area'
  },
  service: {
    title: 'Service charge settings',
    includePrompt: 'Do your prices already include a service charge, or do you add one on top?',
    rateHelp: 'The service charge rate'
  }
} as const;

const MetadataChargeStep = ({ kind, defaults, onBack, onNext }: Props) => {
  const [included, setIncluded] = useState<InclusionStatus | ''>((defaults?.included as any) ?? 'NotApplicable');
  const [rate, setRate] = useState<string>(defaults?.rate != null ? String(defaults.rate) : '');
  const [valid, setValid] = useState(false);

  // If defaults change (e.g., after extraction hydration), ensure null/undefined map to NotApplicable
  useEffect(() => {
    const d = (defaults?.included as any);
    setIncluded(d ?? 'NotApplicable');
  }, [defaults?.included]);

  useEffect(() => {
    const hasChoice = included === 'Included' || included === 'Additional' || included === 'NotApplicable';
    const needsRate = included === 'Included' || included === 'Additional';
    const rateOk = !needsRate || (rate !== '' && !Number.isNaN(Number(rate)) && Number(rate) >= 0 && Number(rate) <= 100);
    setValid(hasChoice && rateOk);
  }, [included, rate]);

  const copy = LABELS[kind];

  return (
    <div className="theme-card p-8 bg-white">
      {/* Heading handled at page level */}
      <div className="mt-6 space-y-4">
        <p className="text-sm text-slate-700">{copy.includePrompt} <span className="text-red-600">*</span></p>
        <div className="flex flex-wrap gap-3">
          {(['Included','Additional','NotApplicable'] as InclusionStatus[]).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setIncluded(opt)}
              className={`inline-flex items-center rounded-full border px-3 py-1.5 text-sm font-medium transition ${included === opt ? 'border-slate-800 bg-slate-900 text-white' : 'border-slate-300 bg-white text-slate-900 hover:bg-slate-50'}`}
            >
              {opt === 'NotApplicable' ? 'Not applicable' : opt}
            </button>
          ))}
        </div>
        {(included === 'Included' || included === 'Additional') && (
          <label className="mt-2 flex max-w-sm flex-col gap-2 text-left">
            <span className="text-sm font-medium text-slate-700">Rate (%)</span>
            <input
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g., 10.1"
              className="rounded-xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 shadow-inner outline-none transition focus:ring-2 focus:ring-[var(--ring)]"
            />
            <span className="text-xs text-slate-600">{copy.rateHelp}</span>
          </label>
        )}
      </div>
      <div className="mt-6 flex items-center justify-between">
        <button type="button" className="btn-secondary" onClick={onBack}>Back</button>
        <button type="button" className="btn-primary" disabled={!valid} onClick={() => onNext({ included: included as InclusionStatus, rate: rate === '' ? undefined : Number(rate) })}>Next</button>
      </div>
    </div>
  );
};

export default MetadataChargeStep;
